import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import styles from "./addBooking.module.css";
import { Typography, Box, Snackbar, Alert, Dialog } from "@mui/material";
import Loader from "../../../components/Loader";
import Step1 from "./Step1";
import Step2 from "./Step2";
import Step3 from "./Step3";
import Step4 from "./Step4";
import { authData, withAdminAuth } from "../../../components/GetAuth";
import { useParams } from "react-router-dom";
import axios from "axios";
import { useWebsite } from "../../../../context/WebsiteContext";
import { useSiteBranding } from "../../../../hooks/useSiteBranding";
import PageHeader from "../../../components/PageHeader";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import DirectionsBoatIcon from "@mui/icons-material/DirectionsBoat";
import { hasFeature } from "../../../components/Features";

const STEP_LABELS = ["Parcel Details", "Pickup Address", "Recipient Address", "Order Summary"];

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (<div
    role="tabpanel"
    hidden={value !== index}
    id={`simple-tabpanel-${index}`}
    aria-labelledby={`simple-tab-${index}`}
    {...other}
  >
    {value === index && (
      <Box sx={{ pt: { xs: 1, sm: 2 } }}>
        <Typography component="div">{children}</Typography>
      </Box>
    )}
  </div>);
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function BookingStepper({ value }) {
  return (
    <div className={styles.Stepper} role="tablist" aria-label="Booking steps">
      {STEP_LABELS.map((label, idx) => {
        const isDone = value > idx;
        const isActive = value === idx;
        return (
          <div
            key={label}
            role="tab"
            aria-selected={isActive}
            aria-current={isActive ? "step" : undefined}
            className={`${styles.StepperItem} ${isActive ? styles.StepperItemActive : ""} ${isDone ? styles.StepperItemDone : ""}`}
          >
            <span className={styles.StepperCircle}>{isDone ? "✓" : idx + 1}</span>
            <span className={styles.StepperLabel}>{label}</span>
          </div>
        );
      })}
    </div>
  );
}

const AddBooking = () => {
  const auth = authData();
  const API_TOKEN = auth?.api_token;
  const { booking_id } = useParams();
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [bookingData, setBookingData] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState("success");
  const [selectPCountry, setSelectPCountry] = useState('GB');
  const [selectDCountry, setSelectDCountry] = useState('GH');
  const [customerEmail, setCustomerEmail] = useState('');
  const [freightModalOpen, setFreightModalOpen] = useState(false);
  const [freightType, setFreightType] = useState("");
  // undefined while loading, null when the company has none
  const [charges, setCharges] = useState(undefined);
  // "Other With Dimension" pricing; null falls back to the default formula
  const [dimensionPricing, setDimensionPricing] = useState(null);

  const { websiteData } = useWebsite();
  useSiteBranding(booking_id ? "Edit booking" : "Add booking", websiteData);

  // Once the parcel has been picked up (status >= 2), Step1 (parcel details)
  // and Step2 (pickup address) become read-only. Only Step3 (recipient /
  // drop-off details) can still be edited.
  const isPickedUp = Number(bookingData?.status) >= 2;

  // Without the Air Freight add-on every booking is Sea Freight, so there is nothing to choose
  const airFreightOn = hasFeature("air_freight");

  useEffect(() => {
    if (!booking_id) {
      if (airFreightOn) {
        setFreightModalOpen(true);
      } else {
        setFreightType('Sea Freight');
        setBookingData(prev => ({ ...prev, freight_type: 'Sea Freight' }));
      }
    }
  }, [booking_id, airFreightOn]);

  useEffect(() => {
    if (auth?.company && auth?.company?.pickup_country) {
      setSelectPCountry(auth?.company?.pickup_country);
    }
    if (auth?.company && auth?.company?.delivery_country) {
      setSelectDCountry(auth?.company?.delivery_country);
    }
  }, [auth?.company]);

  // Company collection/delivery charge settings, applied in the order summary
  useEffect(() => {
    axios.get(process.env.REACT_APP_API_URL + "v1/booking-charges", {
      headers: { Authorization: `Bearer ${API_TOKEN}` }
    }).then((response) => {
      setCharges(response.data.status === "SUCCESS" ? (response.data.data?.charges || null) : null);
      setDimensionPricing(response.data.status === "SUCCESS" ? (response.data.data?.dimension_pricing || null) : null);
    }).catch(() => setCharges(null));
  }, [API_TOKEN]);

  useEffect(() => {
    const fetchBooking = () => {
      setLoading(true);
      axios.get(process.env.REACT_APP_API_URL + "v1/bookings/" + booking_id, {
        headers: {
          Authorization: `Bearer ${API_TOKEN}`
        }
      }).then((response) => {
        setLoading(false);
        let bookingDataTemp = response.data.data;

        setCustomerEmail(bookingDataTemp?.pickup_details?.email);

        setFreightType(bookingDataTemp?.freight_type || 'Sea Freight');

        let products = bookingDataTemp?.products?.map(i => {
          let categoryId = i?.parent_id > 0 ? i?.parent_id : i?.product_id;
          let isItem = i?.parent_id > 0 ? true : false;

          if (i?.length && i?.width && i?.height) {
            categoryId = -1;
          }

          return { category_id: categoryId, is_item: isItem, item_id: i?.product_id, item_name: i?.product_title, total: i?.sub_amount, estimated_value: i?.estimated_value ?? i?.sub_amount, length: i?.length, width: i?.width, height: i?.height };
        });

        let alt_phones_p1 = bookingDataTemp?.pickup_details?.alt_phones?.map(i => {
          return { value: i };
        });

        let alt_phones_r1 = bookingDataTemp?.recipient_details?.alt_phones?.map(i => {
          return { value: i };
        });

        let pickUpData = { address: bookingDataTemp?.pickup_details?.address, customer_id: bookingDataTemp?.pickup_details?.user_id, email: bookingDataTemp?.pickup_details?.email, latitude: parseFloat(bookingDataTemp?.pickup_details?.latitude), longitude: parseFloat(bookingDataTemp?.pickup_details?.longitude), name: bookingDataTemp?.pickup_details?.name, phone: bookingDataTemp?.pickup_details?.phone, post_code: bookingDataTemp?.pickup_details?.post_code, alt_phones: alt_phones_p1 };

        let recipientData = { address: bookingDataTemp?.recipient_details?.address, customer_id: bookingDataTemp?.recipient_details?.user_id, latitude: parseFloat(bookingDataTemp?.recipient_details?.latitude), longitude: parseFloat(bookingDataTemp?.recipient_details?.longitude), name: bookingDataTemp?.recipient_details?.name, phone: bookingDataTemp?.recipient_details?.phone, post_code: bookingDataTemp?.recipient_details?.post_code, alt_phones: alt_phones_r1 };

        setBookingData(prev => {
          return { ...prev, customer_id: bookingDataTemp?.customer_id, registered_company: bookingDataTemp?.registered_company, insurance: bookingDataTemp?.insurance, company_name: bookingDataTemp?.company_name, company_shipping: bookingDataTemp?.company_shipping, pickup_date: bookingDataTemp?.pickup_date, notes: bookingDataTemp?.description, products: products, pickUpData: pickUpData, recipientData: recipientData, discountVal: bookingDataTemp?.discount_val, discountAmount: bookingDataTemp?.discount_amount, status: bookingDataTemp?.status, freight_type: bookingDataTemp?.freight_type || 'Sea Freight' };
        })
      }).catch((error) => {
        setLoading(false);
      });
    }

    if (booking_id) {
      fetchBooking();
    }
  }, [booking_id, API_TOKEN]);

  const saveStep1Data = (data) => {
    let bookingDataTemp = bookingData;
    bookingDataTemp = { ...bookingDataTemp, ...data };

    setBookingData(bookingDataTemp);
    setTabValue((prevValue) => prevValue + 1);
  };

  const saveStep2Data = (pickUpData) => {
    let bookingDataTemp = bookingData;
    bookingDataTemp = { ...bookingDataTemp, pickUpData: pickUpData };

    setBookingData(bookingDataTemp);
    setTabValue((prevValue) => prevValue + 1);
  };

  const saveStep3Data = (recipientData) => {
    let bookingDataTemp = bookingData;
    bookingDataTemp = { ...bookingDataTemp, recipientData: recipientData };

    setBookingData(bookingDataTemp);
    setTabValue((prevValue) => prevValue + 1);
  };

  const backToTab = () => {
    document.documentElement.scrollTo({ top: 0, left: 0, behavior: "instant" });

    setTabValue((prevValue) => prevValue - 1);
  }

  const selectFreightType = (type) => {
    setFreightType(type);
    setBookingData(prev => ({ ...prev, freight_type: type }));
    setFreightModalOpen(false);
  };

  return (<>
    <Loader open={loading} setOpen={setLoading.bind(this)} />

    <div className={`${styles.MainDiv}`}>
      <PageHeader title="Booking" trail={[{ label: booking_id ? 'Edit' : 'Add' }]} />

      <div className={`${styles.MainCard}`}>
        <div className={styles.FreightBadgeWrap}>
          <button
            type="button"
            className={styles.FreightBadge}
            onClick={() => airFreightOn && setFreightModalOpen(true)}
          >
            {freightType === 'Air Freight' ? <FlightTakeoffIcon /> : <DirectionsBoatIcon />}
            {freightType || 'Select Freight Option'}
          </button>
        </div>

        <Box sx={{ width: "100%" }} className={`${styles.AddBookingTab}`}>
          <BookingStepper value={tabValue} />

          <TabPanel value={tabValue} index={0}>
            <Step1 charges={charges} dimensionPricing={dimensionPricing}saveStep1Data={saveStep1Data.bind(this)} bookingData={bookingData} setCustomerEmail={setCustomerEmail.bind(this)} disabled={false} freightType={freightType} />
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <Step2 charges={charges} saveStep2Data={saveStep2Data.bind(this)} backToTab={backToTab.bind(this)} bookingData={bookingData} selectCountry={selectPCountry} customerEmail={customerEmail} disabled={false} />
          </TabPanel>
          <TabPanel value={tabValue} index={2}>
            <Step3 charges={charges} saveStep3Data={saveStep3Data.bind(this)} backToTab={backToTab.bind(this)} bookingData={bookingData} selectCountry={selectDCountry} />
          </TabPanel>
          <TabPanel value={tabValue} index={3}>
            <Step4 booking_id={booking_id} bookingData={bookingData} charges={charges} backToTab={backToTab.bind(this)} setLoading={setLoading.bind(this)} setSnackbarMessage={setSnackbarMessage.bind(this)} setSnackbarType={setSnackbarType.bind(this)} setSnackbarOpen={setSnackbarOpen.bind(this)} />
          </TabPanel>
        </Box>
      </div>
    </div>

    <Dialog
      open={freightModalOpen}
      onClose={() => { if (freightType) setFreightModalOpen(false); }}
      disableEscapeKeyDown={!freightType}
      maxWidth="xs"
      fullWidth
    >
      <div className={styles.FreightModal}>
        <h3 className={styles.FreightModalTitle}>Select Freight Option</h3>
        <p className={styles.FreightModalSub}>How should this shipment travel?</p>
        <div className={styles.FreightOptions}>
          <button
            type="button"
            className={`${styles.FreightOption} ${freightType === 'Air Freight' ? styles.FreightOptionActive : ''}`}
            onClick={() => selectFreightType('Air Freight')}
          >
            <FlightTakeoffIcon />
            <span>Air Freight</span>
            <small>Faster, priced by weight</small>
          </button>
          <button
            type="button"
            className={`${styles.FreightOption} ${freightType === 'Sea Freight' ? styles.FreightOptionActive : ''}`}
            onClick={() => selectFreightType('Sea Freight')}
          >
            <DirectionsBoatIcon />
            <span>Sea Freight</span>
            <small>Economical, priced by volume</small>
          </button>
        </div>
      </div>
    </Dialog>

    <Snackbar
      open={snackbarOpen}
      autoHideDuration={6000}
      onClose={(e) => setSnackbarOpen(false)}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <Alert
        onClose={(e) => setSnackbarOpen(false)}
        severity={snackbarType}
        variant="filled"
        sx={{ width: "100%" }}
      >
        {snackbarMessage}
      </Alert>
    </Snackbar>
  </>);
};

export default withAdminAuth(AddBooking);